import React, { lazy, Suspense, useState, useCallback, useRef } from "react";
import { Box, Snackbar, Alert } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  TableChartRounded,
  PrecisionManufacturingRounded,
  AssignmentRounded,
  AssignmentTurnedInOutlined,
  ImageRounded,
  InsertDriveFileRounded,
} from "@mui/icons-material";
import { ChatMessage, ChatSource, ChatArtifact, ChatArtifactType, SrCitation, DiagramOverlayArtifactData } from "../models/chatBotModels";
import ChatSourceCard from "./ChatSourceCard";
import ChatSrCitationChip from "./ChatSrCitationChip";
import ChatListRefChip from "./ChatListRefChip";
import ChatSuggestionChips from "./ChatSuggestionChips";
import ChatActionButtons from "./ChatActionButtons";
import MidasActionCardWrapper from "./MidasActionCardWrapper";
import ChatContextSwitchButtons from "./ChatContextSwitchButtons";
import MidasLogo from "../../../customer/midas/components/MidasLogo";
import { ImageGalleryLightbox } from "../../components/gallery";
import type { GalleryImage } from "../../components/gallery";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import { openArtifact, openArtifactAtPage } from "../../../customer/midas/midasPageSlice";
import { parseListRefs } from "../streamBotMessage";
import customerMachineServiceReportsApi from "../../../customer/machinery/api/customerMachineServiceReportsApi";
import useTranslation from "../../hooks/useTranslation";
import { dt } from "../../themes/redesignTokens";

// Lazy-load thumbnail component so MIDAS code isn't bundled in the regular chat widget
const MidasArtifactThumbnail = lazy(() => import("../../../customer/midas/components/MidasArtifactThumbnail"));

// ═══════════════════════════════════════════════════════════════
//  MIDAS Design Tokens (local subset)
// ═══════════════════════════════════════════════════════════════


const midas = {
  gold: "#D4A017",
  goldSoft: "rgba(212, 160, 23, 0.08)",
};

// ═══════════════════════════════════════════════════════════════
//  Markdown Pre-Processing
// ═══════════════════════════════════════════════════════════════

/** True if the line is a bold-only header like "**DENNÁ (8 hod)**" */
const isBoldHeaderLine = (line: string): boolean =>
  /^\*\*.+\*\*$/.test(line.trim());

/** True if the line is already a markdown list item (- item, * item, 1. item) */
const isAlreadyListItem = (line: string): boolean =>
  /^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line);

/** True if the line is a markdown heading (# heading) */
const isMarkdownHeading = (line: string): boolean =>
  /^#{1,6}\s+/.test(line.trim());

/**
 * Pre-process bot text so it renders correctly in ReactMarkdown.
 *
 * 0. Convert literal "\n" escape sequences (backslash+n) to real newlines.
 *    This fixes the most common rendering issue: when the bot's JSON payload
 *    is double-serialized or the SSE proxy delivers escaped newlines,
 *    markdown block elements (headings, lists, paragraphs) fail to render
 *    because they require real newline characters as line separators.
 * 1. Convert Unicode bullet characters (•●◦▪▸►) into "- " list syntax.
 * 2. Detect plain-text lines that follow a bold-only header line
 *    (e.g. **DENNÁ (8 hod)**) and convert them into "- " list items.
 *    This handles the common bot pattern of maintenance schedules where
 *    items are sent as plain lines under bold section headers.
 */
const preprocessMarkdown = (text: string): string => {
  if (!text) return "";

  // Step 0: Convert literal "\n" and "\r\n" escape sequences to real newlines.
  // If the text already has real newlines, split("\\n") finds no matches and
  // the string is returned unchanged — so this is always safe.
  let processed = text.split("\\r\\n").join("\n").split("\\n").join("\n");

  // Step 1: Unicode bullet replacement
  processed = processed.replace(/^[•●◦▪▸►]\s*/gm, "- ");

  // Step 2: Convert plain lines under bold headers into list items
  const lines = processed.split("\n");
  const result: string[] = [];
  let afterBoldHeader = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (isBoldHeaderLine(trimmed)) {
      // This is a bold header — keep it, and flag that following lines are list items
      afterBoldHeader = true;
      result.push(line);
    } else if (afterBoldHeader && trimmed.length > 0) {
      // Non-blank line after a bold header — convert to list item if not already one
      if (isAlreadyListItem(trimmed) || isMarkdownHeading(trimmed)) {
        result.push(line);
      } else {
        result.push(`- ${trimmed}`);
      }
    } else {
      // Blank line or normal line before any bold header
      if (trimmed.length === 0) {
        afterBoldHeader = false;
      }
      result.push(line);
    }
  }

  return result.join("\n");
};

// ═══════════════════════════════════════════════════════════════
//  Markdown Styled Components
// ═══════════════════════════════════════════════════════════════

const markdownComponents: object = {
  p: ({ children }: any) => (
    <Box
      component="p"
      sx={{
        margin: 0,
        marginBottom: "8px",
        fontFamily: dt.font,
        fontSize: "14px",
        fontWeight: 400,
        color: dt.textPrimary,
        lineHeight: 1.6,
        "&:last-child": { marginBottom: 0 },
      }}
    >
      {children}
    </Box>
  ),
  strong: ({ children }: any) => (
    <Box component="strong" sx={{ fontWeight: 600 }}>
      {children}
    </Box>
  ),
  em: ({ children }: any) => (
    <Box component="em" sx={{ fontStyle: "italic" }}>
      {children}
    </Box>
  ),
  h1: ({ children }: any) => (
    <Box
      component="h1"
      sx={{
        fontFamily: dt.font,
        fontSize: "16px",
        fontWeight: 700,
        color: dt.textPrimary,
        mt: "12px",
        mb: "6px",
        lineHeight: 1.4,
      }}
    >
      {children}
    </Box>
  ),
  h2: ({ children }: any) => (
    <Box
      component="h2"
      sx={{
        fontFamily: dt.font,
        fontSize: "15px",
        fontWeight: 700,
        color: dt.textPrimary,
        mt: "12px",
        mb: "6px",
        lineHeight: 1.4,
      }}
    >
      {children}
    </Box>
  ),
  h3: ({ children }: any) => (
    <Box
      component="h3"
      sx={{
        fontFamily: dt.font,
        fontSize: "14px",
        fontWeight: 700,
        color: dt.textPrimary,
        mt: "12px",
        mb: "6px",
        lineHeight: 1.4,
      }}
    >
      {children}
    </Box>
  ),
  a: ({ href, children }: any) => (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: dt.textLink,
        textDecoration: "none",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {children}
    </Box>
  ),
  ul: ({ children }: any) => (
    <Box
      component="ul"
      sx={{
        fontFamily: dt.font,
        fontSize: "13px",
        pl: "16px",
        my: "4px",
        "& li::marker": { color: midas.gold },
      }}
    >
      {children}
    </Box>
  ),
  ol: ({ children }: any) => (
    <Box
      component="ol"
      sx={{
        fontFamily: dt.font,
        fontSize: "13px",
        pl: "16px",
        my: "4px",
      }}
    >
      {children}
    </Box>
  ),
  li: ({ children }: any) => (
    <Box component="li" sx={{ my: "4px", lineHeight: 1.5 }}>
      {children}
    </Box>
  ),
  code: ({ inline, children, className }: any) => {
    if (inline) {
      return (
        <Box
          component="code"
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "12px",
            backgroundColor: "#F0F1F3",
            padding: "2px 6px",
            borderRadius: "4px",
          }}
        >
          {children}
        </Box>
      );
    }
    return (
      <Box
        component="pre"
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "12px",
          backgroundColor: "#F8F9FA",
          border: `1px solid ${dt.cardBorder}`,
          borderRadius: "8px",
          padding: "10px 14px",
          overflowX: "auto",
          maxHeight: "200px",
          overflowY: "auto",
          my: "8px",
        }}
      >
        <code className={className}>{children}</code>
      </Box>
    );
  },
  table: ({ children }: any) => (
    <Box
      sx={{
        overflowX: "auto",
        my: "8px",
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: "6px",
      }}
    >
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: dt.font,
          fontSize: "13px",
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }: any) => (
    <Box
      component="thead"
      sx={{
        backgroundColor: dt.headerBg,
        "& th": {
          fontFamily: dt.font,
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          padding: "8px 10px",
          borderBottom: `1px solid ${dt.cardBorder}`,
        },
      }}
    >
      {children}
    </Box>
  ),
  td: ({ children }: any) => (
    <Box
      component="td"
      sx={{
        padding: "8px 10px",
        borderBottom: `1px solid ${dt.cardBorder}`,
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{
        border: "none",
        borderTop: `1px solid ${dt.cardBorder}`,
        my: "12px",
      }}
    />
  ),
};

// ═══════════════════════════════════════════════════════════════
//  Artifact Badge Icons
// ═══════════════════════════════════════════════════════════════

const ARTIFACT_BADGE_ICONS: Record<string, React.ElementType> = {
  table: TableChartRounded,
  machine_card: PrecisionManufacturingRounded,
  sr_summary: AssignmentRounded,
  image: ImageRounded,
  platform_file: InsertDriveFileRounded,
};

const getArtifactBadgeIcon = (type: ChatArtifactType): React.ElementType => {
  return ARTIFACT_BADGE_ICONS[type] || InsertDriveFileRounded;
};

// ═══════════════════════════════════════════════════════════════
//  ChatBotMessage
// ═══════════════════════════════════════════════════════════════

interface ChatBotMessageProps {
  message: ChatMessage;
  showAvatar?: boolean;
  showTimestamp?: boolean;
  onSuggestionClick?: (text: string) => void;
  isPhone?: boolean;
  /** MC-22: kiosk mode renders SR citation chips as non-clickable */
  isKiosk?: boolean;
}

/** Check if a message is in platform mode based on its intent */
const isPlatformMode = (intent?: string): boolean => {
  if (!intent) return false;
  return intent.startsWith("PLATFORM_");
};

const ChatBotMessage: React.FC<ChatBotMessageProps> = ({
  message,
  showAvatar = false,
  showTimestamp = false,
  onSuggestionClick,
  isPhone = false,
  isKiosk = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnMidasPage = location.pathname === "/midas";
  const { translate, i18n } = useTranslation("chatBot");

  // MC-22: active artifact tab (used to highlight the chip whose SR is currently shown)
  const artifactTabs = useAppSelector((s) => s.midasPage.artifactTabs);
  const activeArtifactTabId = useAppSelector((s) => s.midasPage.activeArtifactTabId);
  const activeSrReportNumber = React.useMemo(() => {
    const active = artifactTabs.find((t) => t.id === activeArtifactTabId);
    if (!active) return null;
    if (active.artifact.type !== "sr_detail") return null;
    const data = (active.artifact.data || {}) as { reportNumber?: string };
    return data.reportNumber || null;
  }, [artifactTabs, activeArtifactTabId]);

  // MC-25: active page (highlights list-ref chip whose cross_reference artifact is open)
  const activeRefPage = React.useMemo(() => {
    const active = artifactTabs.find((t) => t.id === activeArtifactTabId);
    if (!active) return null;
    if (active.artifact.source !== "cross_reference") return null;
    const data = (active.artifact.data || {}) as DiagramOverlayArtifactData;
    return typeof data.page === "number" ? data.page : (active.artifact.page ?? null);
  }, [artifactTabs, activeArtifactTabId]);

  // MC-25: viewed list-ref pages (tinted chip once clicked in this session)
  const [viewedRefPages, setViewedRefPages] = useState<Set<number>>(new Set());

  const handleListRefClick = useCallback(
    (payload: { listId: string; page: number; name?: string }) => {
      setViewedRefPages((prev) => {
        const next = new Set(prev);
        next.add(payload.page);
        return next;
      });
      // 1. Try to activate an already-open cross_reference tab for this page.
      //    The reducer auto-opens the panel if closed (per Q3).
      dispatch(openArtifactAtPage({ page: payload.page }));
      // 2. If no tab is currently open for this page, look in this message's
      //    artifacts and open one. `page` is the canonical join key (per Q1).
      const matching = (message.artifacts || []).find((a) => {
        if (a.source !== "cross_reference") return false;
        const data = (a.data || {}) as DiagramOverlayArtifactData;
        const dataPage = typeof data.page === "number" ? data.page : a.page;
        return dataPage === payload.page;
      });
      if (matching) {
        // SEC-REQ-5: validate URL is a relative path if provided
        if (matching.url && !matching.url.startsWith("/") && matching.url !== "") return;
        dispatch(
          openArtifact({
            type: matching.type as any,
            title: matching.title,
            source: matching.source,
            url: matching.url || "",
            document_id: matching.document_id,
            page: matching.page,
            mime_type: matching.mime_type,
            entity_type: matching.entity_type,
            entity_id: matching.entity_id,
            file_id: matching.file_id,
            data: matching.data as any,
          })
        );
      }
    },
    [dispatch, message.artifacts]
  );

  // MC-22: chip click state
  const [resolvingSr, setResolvingSr] = useState<string | null>(null);
  const [viewedSrs, setViewedSrs] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ message: string; severity: "error" | "warning" } | null>(null);

  const handleSrChipClick = useCallback(
    async (citation: SrCitation) => {
      if (resolvingSr) return; // one at a time
      const reportNumber = citation.reportNumber;
      setResolvingSr(reportNumber);
      try {
        const sr = await customerMachineServiceReportsApi.getServiceReport(reportNumber);
        const machineId = (sr as any).machineId;
        if (!machineId) {
          setToast({ message: translate("srCitations.errorGeneric"), severity: "error" });
          return;
        }
        setViewedSrs((prev) => {
          const next = new Set(prev);
          next.add(reportNumber);
          return next;
        });
        if (isOnMidasPage) {
          // Open inside MIDAS artifact pane — dedup happens in the reducer
          // (matches on type + title); use reportNumber as the title so a
          // re-click reactivates the existing tab.
          dispatch(
            openArtifact({
              type: "sr_detail",
              title: reportNumber,
              source: "machineid",
              url: "",
              entity_type: "service_report",
              entity_id: reportNumber,
              data: { reportNumber, machineId },
            })
          );
        } else {
          // Machine-scoped chat: no artifact pane — navigate
          navigate(`/machinery/${machineId}/service-reports/${reportNumber}`);
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setToast({ message: translate("srCitations.errorForbidden"), severity: "warning" });
        } else if (status === 404) {
          setToast({ message: translate("srCitations.errorNotFound"), severity: "warning" });
        } else {
          setToast({ message: translate("srCitations.errorGeneric"), severity: "error" });
        }
      } finally {
        setResolvingSr(null);
      }
    },
    [resolvingSr, isOnMidasPage, dispatch, navigate, translate]
  );

  // In platform mode, sources are not relevant (no document search)
  const showSources =
    !isPlatformMode(message.intent) &&
    message.sources &&
    message.sources.length > 0;

  // ── Lightbox state for source images ──────────────────────────
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Map: source index → blob URL (populated as images load)
  const blobUrlsRef = useRef<Map<number, string>>(new Map());

  const handleSourceImageLoaded = useCallback((index: number, blobUrl: string) => {
    blobUrlsRef.current.set(index, blobUrl);
  }, []);

  /** Create an artifact from a ChatSource and open it in the artifact panel */
  const openSourceAsArtifact = useCallback((source: ChatSource) => {
    if (!source.imageUrl) return;
    // SEC-REQ-5: validate URL is a relative path
    if (!source.imageUrl.startsWith("/")) return;
    dispatch(openArtifact({
      type: "image",
      title: source.document + (source.page ? ` p.${source.page}` : ""),
      source: "bot",
      url: source.imageUrl,
      document_id: source.document,
      page: source.page,
    }));
  }, [dispatch]);

  /** Open a rich artifact in the artifact panel */
  const handleArtifactBadgeClick = useCallback((artifact: ChatArtifact) => {
    // SEC-REQ-5: validate URL is relative if present
    if (artifact.url && !artifact.url.startsWith("/") && artifact.url !== "") return;
    dispatch(openArtifact({
      type: artifact.type as any,
      title: artifact.title,
      source: artifact.source || "bot",
      url: artifact.url || "",
      document_id: artifact.document_id,
      page: artifact.page,
      mime_type: artifact.mime_type,
      entity_type: artifact.entity_type,
      entity_id: artifact.entity_id,
      file_id: artifact.file_id,
      data: artifact.data as any,
    }));
  }, [dispatch]);

  // Rich artifacts to show as inline badges (table, machine_card, sr_summary)
  const richArtifacts = (message.artifacts || []).filter(
    (a) => a.type === "table" || a.type === "machine_card" || a.type === "sr_summary"
  );
  // Image/file artifacts also get badges on /midas
  const allArtifacts = isOnMidasPage ? (message.artifacts || []) : richArtifacts;

  const handleSourceImageClick = useCallback((clickedBlobUrl: string) => {
    if (isOnMidasPage) {
      // On /midas: open in artifact panel instead of lightbox
      // Find the source that corresponds to this blob URL
      const entries = Array.from(blobUrlsRef.current.entries()).sort(([a], [b]) => a - b);
      const match = entries.find(([, url]) => url === clickedBlobUrl);
      if (match && message.sources) {
        const source = message.sources[match[0]];
        if (source) {
          openSourceAsArtifact(source);
          return;
        }
      }
    }
    // Default: open lightbox
    const entries = Array.from(blobUrlsRef.current.entries()).sort(([a], [b]) => a - b);
    const galleryIdx = entries.findIndex(([, url]) => url === clickedBlobUrl);
    setLightboxIndex(galleryIdx >= 0 ? galleryIdx : 0);
    setLightboxOpen(true);
  }, [isOnMidasPage, message.sources, openSourceAsArtifact]);

  const getLightboxImages = useCallback((): GalleryImage[] => {
    const entries = Array.from(blobUrlsRef.current.entries()).sort(([a], [b]) => a - b);
    return entries.map(([idx, url]) => {
      const src = message.sources?.[idx];
      return {
        id: `source-${idx}`,
        src: url,
        alt: src?.document
          ? `${src.document} — ${translate("source.page", { page: src.page || "" })}`
          : translate("source.imageAlt", { index: idx + 1 }),
        name: src?.document || `source-image-${idx + 1}`,
      };
    });
  }, [message.sources, translate]);

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    // Active UI language (not the browser's), 24 h like every other platform time (HH:mm).
    return d.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Avatar (only on first message in group) */}
      {showAvatar && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
          }}
        >
          <MidasLogo size="sm" animated="none" />
        </Box>
      )}

      {/* Message bubble */}
      <Box
        sx={{
          ml: "36px",
          maxWidth: isPhone ? "90%" : "85%",
          backgroundColor: midas.goldSoft,
          border: "1px solid rgba(212, 160, 23, 0.12)",
          borderRadius: "4px 14px 14px 14px",
          padding: "12px 16px",
          animation: "midasBotMsg 0.25s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        {/* MC-25: Split the message text by <list-ref> tags so each tag renders
            as an inline ChatListRefChip. Plain-text segments go through the
            same markdown pipeline as before. Messages with NO <list-ref> tags
            take the single-segment fast path (identical to pre-MC-25 behavior). */}
        {(() => {
          const segments = parseListRefs(message.text);
          // Fast path: no tags → render exactly as before (single markdown block)
          if (segments.length <= 1 && (segments.length === 0 || segments[0].kind === "text")) {
            return (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {preprocessMarkdown(message.text)}
              </ReactMarkdown>
            );
          }
          // Mixed content: render each text segment through markdown, each
          // list-ref as an inline chip. Use `Box component="span"` wrappers so
          // neighbouring text flows naturally around the chip.
          return (
            <Box sx={{ display: "inline" }}>
              {segments.map((seg, idx) => {
                if (seg.kind === "text") {
                  return (
                    <Box key={idx} component="span" sx={{ "& > p": { display: "inline", margin: 0 } }}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                      >
                        {preprocessMarkdown(seg.text)}
                      </ReactMarkdown>
                    </Box>
                  );
                }
                // list-ref segment
                return (
                  <ChatListRefChip
                    key={idx}
                    listId={seg.listId}
                    page={seg.page}
                    name={seg.name}
                    label={seg.label}
                    isActive={activeRefPage === seg.page}
                    isViewed={viewedRefPages.has(seg.page)}
                    onClick={isKiosk ? undefined : handleListRefClick}
                  />
                );
              })}
            </Box>
          );
        })()}

        {/* Artifact rendering: thumbnail cards on /midas, badge pills on regular widget */}
        {allArtifacts.length > 0 && (
          isOnMidasPage ? (
            <MidasArtifactThumbnailRow artifacts={allArtifacts} />
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "8px" }}>
              {allArtifacts.map((artifact, idx) => {
                const Icon = getArtifactBadgeIcon(artifact.type);
                return (
                  <Box
                    key={idx}
                    onClick={() => handleArtifactBadgeClick(artifact)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      px: "10px",
                      py: "4px",
                      borderRadius: dt.radiusPill,
                      backgroundColor: dt.cardBg,
                      border: `1px solid ${midas.gold}`,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: midas.goldSoft,
                        boxShadow: "0 1px 6px rgba(212, 160, 23, 0.15)",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: "14px", color: midas.gold }} />
                    <Box
                      sx={{
                        fontFamily: dt.font,
                        fontSize: "11px",
                        fontWeight: 600,
                        color: dt.textPrimary,
                        maxWidth: "140px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {/* SEC-REQ-7: render as plain text only */}
                      {artifact.title}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )
        )}

        {/* Source cards — only shown in machine mode, hidden on /midas (artifacts accessed via panel) */}
        {showSources && !isOnMidasPage && (() => {
          const sources = message.sources!;
          const imageSources = sources.map((s, i) => ({ source: s, idx: i })).filter(({ source }) => !!source.imageUrl);
          const textSources = sources.map((s, i) => ({ source: s, idx: i })).filter(({ source }) => !source.imageUrl);
          const extraImageCount = imageSources.length > 1 ? imageSources.length - 1 : 0;

          return (
            <Box sx={{ mt: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
              {/* Image sources: 1 full card + "+N" pill */}
              {imageSources.length > 0 && (
                <Box sx={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
                  {/* First image card — always visible */}
                  <ChatSourceCard
                    source={imageSources[0].source}
                    onImageClick={handleSourceImageClick}
                    onImageLoaded={(blobUrl) => handleSourceImageLoaded(imageSources[0].idx, blobUrl)}
                  />
                  {/* "+N more" pill */}
                  {extraImageCount > 0 && (
                    <Box
                      onClick={() => {
                        // Open gallery at 2nd image
                        const entries = Array.from(blobUrlsRef.current.entries()).sort(([a], [b]) => a - b);
                        if (entries.length > 1) {
                          setLightboxIndex(1);
                          setLightboxOpen(true);
                        }
                      }}
                      sx={{
                        minWidth: "56px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: dt.cardBg,
                        border: `1px solid ${midas.gold}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        px: "12px",
                        "&:hover": {
                          backgroundColor: midas.goldSoft,
                          boxShadow: "0 2px 8px rgba(212, 160, 23, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ fontFamily: dt.fontMono, fontSize: "16px", fontWeight: 700, color: midas.gold }}>
                        +{extraImageCount}
                      </Box>
                      <Box sx={{ fontFamily: dt.font, fontSize: "10px", color: dt.textSecondary, mt: "2px" }}>
                        {translate("thumbnailsMore")}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {/* Hidden cards for remaining image sources — needed to load blob URLs */}
              {imageSources.slice(1).map(({ source, idx }) => (
                <ChatSourceCard
                  key={`hidden-${idx}`}
                  source={source}
                  onImageClick={handleSourceImageClick}
                  onImageLoaded={(blobUrl) => handleSourceImageLoaded(idx, blobUrl)}
                  hidden
                />
              ))}
              {/* Text-only sources — render normally */}
              {textSources.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    overflowX: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {textSources.map(({ source, idx }) => (
                    <ChatSourceCard key={idx} source={source} />
                  ))}
                </Box>
              )}
            </Box>
          );
        })()}

        {/* MC-22: Service report citations — renders in both /midas and machine chat.
            Click opens artifact pane on /midas, navigates on machine chat.
            Kiosk renders non-clickable chips (no onClick passed). */}
        {message.srCitations && message.srCitations.length > 0 && (
          <Box sx={{ mt: "10px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "6px" }}>
              <AssignmentTurnedInOutlined sx={{ fontSize: "12px", color: midas.gold }} />
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 600,
                  color: dt.textSecondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                {translate("srCitations.heading")}
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {message.srCitations.map((c) => (
                <ChatSrCitationChip
                  key={c.reportNumber}
                  citation={c}
                  isActive={activeSrReportNumber === c.reportNumber}
                  isViewed={viewedSrs.has(c.reportNumber)}
                  isResolving={resolvingSr === c.reportNumber}
                  isKiosk={isKiosk}
                  onClick={isKiosk ? undefined : handleSrChipClick}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Timestamp */}
      {showTimestamp && (
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "10px",
            fontWeight: 400,
            color: dt.textMuted,
            mt: "4px",
            ml: "36px",
          }}
        >
          {formatTime(message.timestamp)}
        </Box>
      )}

      {/* Suggestion chips — hidden on MIDAS fullscreen page */}
      {!isOnMidasPage && message.suggestions && message.suggestions.length > 0 && onSuggestionClick && (
        <ChatSuggestionChips
          suggestions={message.suggestions}
          onChipClick={onSuggestionClick}
          isPhone={isPhone}
        />
      )}

      {/* Action buttons — rendered below bot messages with actions[] */}
      {message.actions && message.actions.length > 0 && (
        <Box sx={{ ml: "36px", mt: "6px" }}>
          <ChatActionButtons
            messageId={message.id}
            actions={message.actions}
          />
        </Box>
      )}

      {/* MA-0: Action card — rendered when message has actionRequest (from intercept or SSE) */}
      {message.actionRequest && (
        <MidasActionCardWrapper actionRequest={message.actionRequest} />
      )}

      {/* Tier-0 context switch chips — rendered for nav.switch_machine / nav.switch_platform actions */}
      {message.uiActionRequests && message.uiActionRequests.length > 0 && (
        <ChatContextSwitchButtons uiActions={message.uiActionRequests} />
      )}

      {/* Image lightbox for source thumbnails — hidden on /midas (artifacts accessed via panel) */}
      {showSources && !isOnMidasPage && (
        <ImageGalleryLightbox
          images={getLightboxImages()}
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          initialIndex={lightboxIndex}
        />
      )}

      {/* MC-22: SR citation error toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ fontFamily: dt.font, fontSize: "13px" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ART-5: Horizontal thumbnail row for /midas (with expand toggle)
// ═══════════════════════════════════════════════════════════════

const MAX_VISIBLE_THUMBNAILS = 5;

const MidasArtifactThumbnailRow: React.FC<{ artifacts: ChatArtifact[] }> = ({ artifacts }) => {
  const { translate } = useTranslation("chatBot");
  const [expanded, setExpanded] = useState(false);

  const visible = expanded ? artifacts : artifacts.slice(0, MAX_VISIBLE_THUMBNAILS);
  const hiddenCount = artifacts.length - MAX_VISIBLE_THUMBNAILS;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        mt: "10px",
        "@media (max-width: 600px)": {
          "& > *": { maxWidth: "calc(50% - 4px)" },
        },
      }}
    >
      <Suspense fallback={null}>
        {visible.map((artifact, idx) => (
          <MidasArtifactThumbnail key={idx} artifact={artifact} />
        ))}
      </Suspense>
      {!expanded && hiddenCount > 0 && (
        <Box
          onClick={() => setExpanded(true)}
          sx={{
            width: "148px",
            minWidth: "148px",
            height: "106px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: dt.radiusSm,
            border: `1px dashed ${midas.gold}`,
            backgroundColor: midas.goldSoft,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(212, 160, 23, 0.12)",
              borderStyle: "solid",
            },
          }}
        >
          <Box sx={{ fontFamily: dt.fontMono, fontSize: "16px", fontWeight: 700, color: midas.gold }}>
            +{hiddenCount}
          </Box>
          <Box sx={{ fontFamily: dt.font, fontSize: "10px", color: dt.textSecondary, mt: "2px" }}>
            {translate("thumbnailsMore")}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ChatBotMessage;
